import { isRawTable } from './tableTypes';
import { parseExternalReference } from './externalReferences';

const PERIOD_MAPPING_KEY = 'period';

const TYPE_TEXT = 'Text';
const TYPE_NUMBER = 'Number';
const TYPE_DATE = 'Date';
const TYPE_BOOLEAN = 'Yes/No';
const CANONICAL_TYPES = new Set([TYPE_TEXT, TYPE_NUMBER, TYPE_DATE, TYPE_BOOLEAN]);

// Mirror of the backend's normalize_declared_type alias map (column_types.py).
// Anything unrecognised resolves to Text, which is never warned on.
const TYPE_ALIASES = {
  text: TYPE_TEXT, string: TYPE_TEXT, str: TYPE_TEXT,
  number: TYPE_NUMBER, float: TYPE_NUMBER, float64: TYPE_NUMBER,
  integer: TYPE_NUMBER, int: TYPE_NUMBER, numeric: TYPE_NUMBER,
  date: TYPE_DATE, datetime: TYPE_DATE, timestamp: TYPE_DATE,
  'yes/no': TYPE_BOOLEAN, boolean: TYPE_BOOLEAN, bool: TYPE_BOOLEAN,
};

// Mirror of the backend's FORMULA_OPERATOR_TOKENS (spreadsheet.py).
const FORMULA_OPERATOR_TOKENS = new Set(['+', '-', '*', '/', '(', ')', 'abs', 'abs:']);

function normalizeDeclaredType(typeName) {
  if (!typeName) return TYPE_TEXT;
  if (CANONICAL_TYPES.has(typeName)) return typeName;
  return TYPE_ALIASES[String(typeName).trim().toLowerCase()] || TYPE_TEXT;
}

// Mirror of the backend's get_formula_variables: every backtick-delimited
// token that is not an operator, in stored order.
export function getFormulaVariables(formula) {
  return String(formula)
    .split('`')
    .map((part) => part.trim())
    .filter((token) => token && !FORMULA_OPERATOR_TOKENS.has(token));
}

// A stored mapping value is a formula when it carries a backtick-delimited
// operator: "Gross Premium `-` Commission".
export function isFormulaMapping(value) {
  return typeof value === 'string' && value.includes('`');
}

// A stored formula as the FormulaBuilder's element list. The stored form
// alternates operands with backtick-wrapped operators, so the even parts are
// operands (empty between two operators) and the odd parts are operators.
// 'abs:' normalises to 'abs', as the backend's clean_formula does.
export function formulaToElements(formula) {
  const parts = String(formula).split('`');
  const elements = [];
  parts.forEach((part, index) => {
    const token = part.trim();
    if (index % 2 === 0) {
      if (token) elements.push({ type: 'variable', value: token });
      return;
    }
    elements.push({ type: 'operator', value: token === 'abs:' ? 'abs' : token });
  });
  return elements;
}

// The FormulaBuilder's element list in the stored form, every operator
// between backticks: "Gross Premium `-` Commission". The spaces around an
// operator are trimmed by every reader; an operand's own spaces are kept,
// since they are part of the column name.
export function elementsToFormula(elements) {
  return (elements || [])
    .map((element) => (element.type === 'operator' ? ` \`${element.value}\` ` : element.value))
    .join('')
    .trim();
}

// A formula without the spacing around its backtick operators, so the editor's
// "a `+` b" and the backend's "a`+`b" compare equal.
export function normalizeFormula(value) {
  return String(value).split('`').map((part) => part.trim()).join('`');
}

// Whether two mapping values name the same source. Empty, null and
// undefined are all Not Present; formulas compare without their spacing.
export function sameMappingValue(left, right) {
  const leftMapped = isMappedValue(left);
  const rightMapped = isMappedValue(right);
  if (!leftMapped || !rightMapped) return leftMapped === rightMapped;
  if (isFormulaMapping(left) || isFormulaMapping(right)) {
    return normalizeFormula(left) === normalizeFormula(right);
  }
  return left === right;
}

// The destination columns a source may be mapped onto, in editor order — the same
// key set create_table seeds into possible_values: identifier, detail columns, the
// period key (a time-series table with metrics), then every metric leaf (period-prefixed).
// Raw tables carry neither identifier nor metrics. Any extra
// possible_values key is appended so a table whose learning store diverged from its
// schema still offers what it did before. Mirror of the backend's
// mapping_target_columns (ingestion/cleaning/rule_defs.py). The schema is the authority for what can
// be mapped; possible_values is only the learned vocabulary per target — a wiped or
// partial store must still present every column with an empty selection.
export function getMappingTargetColumns(table) {
  if (!table) return [];
  const targets = [];
  const push = (name) => { if (name && !targets.includes(name)) targets.push(name); };
  const raw = isRawTable(table);

  if (!raw) push((table.identifier || {}).name);
  (table.detailColumns || []).forEach((column) => push(column && column.name));

  // The period dates a unit's metrics, so a table without metrics has no period to map.
  const metricLeaves = raw ? [] : metricLeafTargets(table);
  if (metricLeaves.length > 0) push(PERIOD_MAPPING_KEY);
  metricLeaves.forEach(push);

  Object.keys(table.possible_values || {}).forEach(push);
  return targets;
}

// Every metric leaf of a time-series table as a mapping target, in schema order:
// the dotted path through any group, period-prefixed.
function metricLeafTargets(table) {
  const leaves = [];
  const walk = (schema, path) => {
    Object.entries(schema || {}).forEach(([name, cfg]) => {
      if (!cfg || typeof cfg !== 'object') return;
      if ('type' in cfg) leaves.push(`${PERIOD_MAPPING_KEY}.${[...path, name].join('.')}`);
      else walk(cfg, [...path, name]);
    });
  };
  walk((table.metricColumns || {}).schema, []);
  return leaves;
}

// Reorder `items` to follow `orderList`: the listed ones first, in that order;
// anything unlisted keeps its original position after them. The same rule the
// Data grid applies to a table's display_order.
export function applyOrderWithFallback(items, orderList) {
  if (!Array.isArray(items)) return [];
  if (!Array.isArray(orderList) || orderList.length === 0) return items;
  const remaining = new Set(items);
  const ordered = [];
  orderList.forEach((key) => {
    if (remaining.has(key)) {
      ordered.push(key);
      remaining.delete(key);
    }
  });
  items.forEach((key) => {
    if (remaining.has(key)) ordered.push(key);
  });
  return ordered;
}

// The mapping targets in the order the Data grid draws the table's columns:
// identifier, details per display_order.detail, the period key (when there are
// metrics for it to date), metric leaves
// per display_order.metric (whose entries name a leaf's dotted path without
// the period prefix), then any learned extra key. Each group falls back to the
// schema's order where display_order does not name it. Every destination-facing
// review pane — Mapping Used, the mapping visualiser, a raw table's appended
// rows — lists columns in this order, so they agree with the grid and with each
// other (L47).
export function getDisplayOrderedMappingTargets(table) {
  if (!table) return [];
  const displayOrder = table.display_order || {};
  const raw = isRawTable(table);
  const targets = [];
  const push = (name) => { if (name && !targets.includes(name)) targets.push(name); };

  if (!raw) push((table.identifier || {}).name);
  const detailNames = (table.detailColumns || []).map((column) => column && column.name).filter(Boolean);
  applyOrderWithFallback(detailNames, displayOrder.detail).forEach(push);

  const metricLeaves = raw ? [] : metricLeafTargets(table);
  if (metricLeaves.length > 0) push(PERIOD_MAPPING_KEY);
  const metricOrder = (Array.isArray(displayOrder.metric) ? displayOrder.metric : [])
    .map((name) => `${PERIOD_MAPPING_KEY}.${name}`);
  applyOrderWithFallback(metricLeaves, metricOrder).forEach(push);

  Object.keys(table.possible_values || {}).forEach(push);
  return targets;
}

// Structural mirror of the backend's build_table_column_type_map
// (column_types.py), with one deliberate omission: the tag-category fallback
// for untyped attribute columns. The dialog does not load tag categories, and
// a column that resolves to no type defaults to Text — meaning it is simply
// never warned on. Conservative by construction.
export function buildDestinationTypeMap(table) {
  const map = {};
  if (!table) return map;

  const identifier = table.identifier || {};
  if (identifier.name) map[identifier.name] = normalizeDeclaredType(identifier.type);

  ['detailColumns', 'attributeColumns', 'tableAttributeColumns'].forEach((key) => {
    (table[key] || []).forEach((column) => {
      if (column && column.name) map[column.name] = normalizeDeclaredType(column.type);
    });
  });

  if (table.tableType === 'timeSeries') {
    // Backend stamps the period column as Date for timeSeries tables.
    map[PERIOD_MAPPING_KEY] = TYPE_DATE;
  }

  // Metric leaves, a time-series table's only: 2-layer {name: {type}} and 3-layer
  // {group: {name: {type}}}, period-prefixed — matching the possible_values key naming.
  const metricSchema = (table.metricColumns || {}).schema || {};
  Object.entries(metricSchema).forEach(([name, cfg]) => {
    if (cfg && typeof cfg === 'object' && 'type' in cfg) {
      map[`${PERIOD_MAPPING_KEY}.${name}`] = normalizeDeclaredType(cfg.type);
    } else if (cfg && typeof cfg === 'object') {
      Object.entries(cfg).forEach(([child, childCfg]) => {
        if (childCfg && typeof childCfg === 'object' && 'type' in childCfg) {
          map[`${PERIOD_MAPPING_KEY}.${name}.${child}`] = normalizeDeclaredType(childCfg.type);
        }
      });
    }
  });

  return map;
}

// Derive advisory type warnings from the LIVE mapping state and the unit's
// column_stats, which the backend measures over the first rows of the base:
//   {column: {present, scanned, rows, fails: {Number | Date | Yes/No: {count, examples}}}}
// `present` counts the values that are not missing among the `scanned` rows.
// Pure lookups — no parsing, no coercion logic here. Returns [] when either
// the mapping or the statistics are absent (a unit whose base has no body).
export function deriveTypeWarnings(mapping, table, columnStats) {
  if (!mapping || !table || !columnStats || Object.keys(columnStats).length === 0) {
    return [];
  }
  const typeMap = buildDestinationTypeMap(table);
  const warnings = [];

  const push = (column, declaredType, kind, source, stats, typeFails) => {
    warnings.push({
      column,
      declaredType,
      kind,                                   // 'direct' | 'formula'
      source,                                 // the source column (or formula operand)
      failures: typeFails.count,
      present: stats.present,
      scanned: stats.scanned,
      rows: stats.rows,
      examples: typeFails.examples || [],
      allFail: stats.present > 0 && typeFails.count === stats.present,
    });
  };

  Object.entries(mapping).forEach(([column, value]) => {
    const declaredType = typeMap[column];
    if (!declaredType || declaredType === TYPE_TEXT) return;
    if (!isMappedValue(value)) return;
    const text = String(value);

    if (isFormulaMapping(text)) {
      // Formula: operands are coerced numerically downstream, so a non-numeric
      // operand value yields an EMPTY computed cell. Warn per operand.
      getFormulaVariables(text).forEach((operand) => {
        const stats = columnStats[operand];
        const typeFails = stats && stats.fails && stats.fails[TYPE_NUMBER];
        if (typeFails && typeFails.count > 0) {
          push(column, declaredType, 'formula', operand, stats, typeFails);
        }
      });
      return;
    }

    if (text.startsWith(EXTERNAL_PREFIX)) return;   // scalar broadcasts — out of scope

    const stats = columnStats[text];
    const typeFails = stats && stats.fails && stats.fails[declaredType];
    if (typeFails && typeFails.count > 0) {
      push(column, declaredType, 'direct', text, stats, typeFails);
    }
  });

  return warnings;
}

const EXTERNAL_PREFIX = 'External >> ';

// Mirror of the backend's resolve_scoped_value (spreadsheet.py) over a unit's
// copies: its own sheet's fields for Sheet scope, and its `pool` (the file's
// pool, values only) for document and named-sheet scope. Returns undefined —
// never null — for an unresolvable reference, so a caller can tell "no such
// key" from a key whose value genuinely is null.
function resolveScopedValue(reference, fields, pool) {
  const { scope, key } = parseExternalReference(reference);
  if (scope === 'Sheet') {
    return Object.prototype.hasOwnProperty.call(fields || {}, key) ? fields[key] : undefined;
  }
  return Object.prototype.hasOwnProperty.call(pool || {}, reference) ? pool[reference] : undefined;
}

// The same tokens getFormulaVariables reads, in the same order, with the
// operators kept: {type: 'operator' | 'operand', value}. 'abs:' normalises to
// 'abs', as the backend's clean_formula does.
function tokenizeFormula(formula) {
  return String(formula)
    .split('`')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((token) => (FORMULA_OPERATOR_TOKENS.has(token)
      ? { type: 'operator', value: token === 'abs:' ? 'abs' : token }
      : { type: 'operand', value: token }));
}

// A deliberately small recursive-descent evaluator over the backtick grammar —
//   expr   := term (('+' | '-') term)*
//   term   := factor (('*' | '/') factor)*
//   factor := 'abs' factor | '(' expr ')' | operand
// never eval(). Operand values come from the caller's resolver. Anything the
// grammar rejects (unbalanced parens, a dangling operator, an empty formula)
// and any result that is not a finite number yield null — the empty cell the
// backend writes when a formula cannot be computed. Never throws.
function evaluateFormula(formula, resolveOperand) {
  const tokens = tokenizeFormula(formula);
  let index = 0;

  function atOperator(...values) {
    const token = tokens[index];
    return !!token && token.type === 'operator' && values.includes(token.value);
  }

  function parseFactor() {
    const token = tokens[index];
    if (!token) throw new Error('Formula ended early');
    index += 1;
    if (token.type === 'operand') return resolveOperand(token.value);
    if (token.value === 'abs') return Math.abs(parseFactor());
    if (token.value === '(') {
      const value = parseExpression();
      if (!atOperator(')')) throw new Error('Unbalanced parentheses');
      index += 1;
      return value;
    }
    throw new Error(`Unexpected operator '${token.value}'`);
  }

  function parseTerm() {
    let value = parseFactor();
    while (atOperator('*', '/')) {
      const operator = tokens[index].value;
      index += 1;
      const right = parseFactor();
      value = operator === '*' ? value * right : value / right;
    }
    return value;
  }

  function parseExpression() {
    let value = parseTerm();
    while (atOperator('+', '-')) {
      const operator = tokens[index].value;
      index += 1;
      const right = parseTerm();
      value = operator === '+' ? value + right : value - right;
    }
    return value;
  }

  try {
    if (tokens.length === 0) return null;
    const result = parseExpression();
    if (index !== tokens.length) throw new Error('Trailing tokens');
    if (typeof result !== 'number' || !Number.isFinite(result)) return null;
    return Math.round(result * 10000) / 10000;   // backend rounds floats to 4dp
  } catch (err) {
    return null;
  }
}

function parseFormulaNumber(value) {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return NaN;
  let text = value.trim().replace(/[£$€]/g, '').trim();
  if (text.startsWith('(') && text.endsWith(')')) text = `-${text.slice(1, -1).trim()}`;

  // Same precedence as column_types.parse_number: mixed separators settle
  // the locale; a lone 1,234 is grouped while a lone 1.234 stays decimal.
  if (/^[+-]?[0-9]{1,3}(\.[0-9]{3})+,[0-9]+$/.test(text)) {
    text = text.replace(/\./g, '').replace(',', '.');
  } else if (
    /^[+-]?[0-9]{1,3}(,[0-9]{3})+\.[0-9]*$/.test(text)
    || /^[+-]?[0-9]{1,3}(,[0-9]{3})+$/.test(text)
    || /^[+-]?[0-9]{1,3}(,[0-9]{3})+\.[0-9eE+-]*$/.test(text)
  ) {
    text = text.replace(/,/g, '');
  } else if (/^[+-]?[0-9]+\.[0-9]+$/.test(text)) {
    return Number(text);
  } else if (/^[+-]?[0-9]{1,3}(\.[0-9]{3})+$/.test(text)) {
    text = text.replace(/\./g, '');
  } else if (/^[+-]?[0-9]+,[0-9]+$/.test(text)) {
    text = text.replace(',', '.');
  }

  // Number() also accepts blanks, hexadecimal and booleans. Those are not
  // numeric formula operands under the backend's parser.
  if (!/^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/.test(text)) return NaN;
  return Number(text);
}

// The live mapped sample: a unit's sample rows put through the LIVE mapping,
// so the review surface can show what a draft mapping or formula yields
// before it is applied. A preview-grade mirror of transform_dataframe +
// handle_formula_columns (spreadsheet.py): same column set, same attribute
// merge, same arithmetic. `fields` is the unit's own sheet's fields, `pool`
// the file's pool as the unit copies it (values only), `attributes` the
// unit's attributes (its file's tags merged over the table's attributes).
// Direct and External values are passed through RAW — no type coercion, no
// date normalisation, no number cleanup: the type notes carry the
// parse-quality signal, and coercion stays a backend concern.
export function deriveMappedSample({ rows, mapping, fields, pool, table, attributes }) {
  if (!Array.isArray(rows) || rows.length === 0 || !table) return [];

  const liveMapping = mapping || {};
  const columns = getMappingTargetColumns(table)
    .filter((column) => isMappedValue(liveMapping[column]));
  if (columns.length === 0) return [];

  // Mirror of merge_table_attributes: the table's own attribute values, then
  // the given attributes over the top — a blank value never clobbers a table
  // value. Broadcast onto every row. A unit's attributes are already this
  // merge, so merging them again changes nothing.
  const broadcast = {};
  (table.tableAttributeColumns || []).forEach((column) => {
    if (column && column.name) broadcast[column.name] = column.value;
  });
  Object.entries(attributes || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') broadcast[key] = value;
  });

  const resolveOperand = (row, token) => {
    let value;
    if (Object.prototype.hasOwnProperty.call(broadcast, token)) {
      value = broadcast[token];
    } else if (token.startsWith(EXTERNAL_PREFIX)) {
      value = resolveScopedValue(token, fields, pool);
    } else {
      value = row[token];
    }
    // A missing input makes ordinary arithmetic undefined, including a
    // missing scalar constant. Zero is present and must remain zero.
    return parseFormulaNumber(value);
  };

  const resolveCell = (row, mappingValue) => {
    const text = String(mappingValue);
    if (isFormulaMapping(text)) {
      return evaluateFormula(text, (token) => resolveOperand(row, token));
    }
    if (text.startsWith(EXTERNAL_PREFIX)) {
      const value = resolveScopedValue(text, fields, pool);
      return value === undefined ? null : value;
    }
    const value = row[text];
    return value === undefined ? null : value;
  };

  return rows.map((sourceRow) => {
    const row = sourceRow || {};
    const record = {};
    columns.forEach((column) => { record[column] = resolveCell(row, liveMapping[column]); });
    // Attributes are written after the mappings, exactly as the backend does —
    // an attribute sharing a mapped column's name wins.
    Object.entries(broadcast).forEach(([key, value]) => { record[key] = value; });
    return record;
  });
}

function isMappedValue(value) {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  return normalized !== '' && normalized.toLowerCase() !== 'not present';
}

export function mappingCoversColumn(mapping, columnName) {
  if (!mapping || !columnName) return false;
  return isMappedValue(mapping[columnName]);
}

// The two scoped required flags of every detail column (a raw table's data
// columns) and every metric leaf, keyed by mapping target (Spec 6 section
// 2.2): {target: {perFile, perUnit}}. "Required per file" means some unit of
// the file must supply the value for each identifier; "required per unit"
// means every unit must. Attribute columns keep their own upload-tag
// requirement and are not here.
export function buildRequiredScopeMap(table) {
  const scopes = {};
  if (!table) return scopes;
  (table.detailColumns || []).forEach((column) => {
    if (!column || !column.name) return;
    scopes[column.name] = {
      perFile: column.mandatory_per_file === true,
      perUnit: column.mandatory_per_unit === true,
    };
  });
  if (isRawTable(table)) return scopes;

  // A time-series table's metric leaves, period-prefixed.
  const walk = (schema, path) => {
    Object.entries(schema || {}).forEach(([name, cfg]) => {
      if (!cfg || typeof cfg !== 'object') return;
      if (!('type' in cfg)) {
        walk(cfg, [...path, name]);
        return;
      }
      scopes[`${PERIOD_MAPPING_KEY}.${[...path, name].join('.')}`] = {
        perFile: cfg.mandatory_per_file === true,
        perUnit: cfg.mandatory_per_unit === true,
      };
    });
  };
  walk((table.metricColumns || {}).schema, []);
  return scopes;
}

// The mapping targets a unit's mapping must still fill before the unit can
// be approved — the mapping half of the backend's gate_blockers
// (statuses.py), as a list of targets rather than sentences:
//   * a time-series table: its identifier, every detail column and metric
//     leaf required per unit, and the period once any metric is mapped;
//   * a raw table: every data column required in either scope, since raw
//     rows are independent.
// A column required per file only may be left unmapped in one unit, since a
// sibling unit can supply it. Attribute columns come from the upload's tags
// and are never mapped.
export function getUnmappedMandatoryTargets(mapping, table) {
  if (!table) return [];
  const raw = isRawTable(table);
  const isTimeSeries = table.tableType === 'timeSeries';
  const required = [];
  if (isTimeSeries && (table.identifier || {}).name) required.push(table.identifier.name);

  const scopes = buildRequiredScopeMap(table);
  Object.entries(scopes).forEach(([target, scope]) => {
    const requiredHere = raw ? (scope.perFile || scope.perUnit) : scope.perUnit;
    if (requiredHere) required.push(target);
  });

  const metricMapped = Object.entries(mapping || {}).some(([target, value]) => (
    target.startsWith(`${PERIOD_MAPPING_KEY}.`) && isMappedValue(value)
  ));
  if (isTimeSeries && metricMapped) required.push(PERIOD_MAPPING_KEY);

  return required.filter((target) => !mappingCoversColumn(mapping, target));
}

// Mirror of the backend's _normalize_for_fuzzy: collapse whitespace, casefold.
const normalizeForFuzzy = (value) => String(value).split(/\s+/).join(' ').trim().toLowerCase();

// Levenshtein with substitutions costing 2 — the backend's fuzz.ratio formula:
// ratio = 100 * ((len(a) + len(b)) - distance) / (len(a) + len(b)).
const fuzzyRatio = (a, b) => {
  const lensum = a.length + b.length;
  if (lensum === 0) return 100;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 2);
      row.push(Math.min(prev[j] + 1, row[j - 1] + 1, substitution));
    }
    prev = row;
  }
  const ratio = (100 * (lensum - prev[b.length])) / lensum;
  // fuzzywuzzy returns an integer, using Python's round-to-even at a tie.
  const lower = Math.floor(ratio);
  return ratio === lower + 0.5 ? lower + (lower % 2) : Math.round(ratio);
};

const FUZZY_LEARNED_THRESHOLD = 99;

// Is this mapping value replayable from the column's learned possible values?
// A formula is learned when a learned formula is the same formula, spacing
// aside; a simple value when a learned simple value matches it near-exactly.
export const isLearnedMapping = (value, candidates) => {
  if (isFormulaMapping(String(value))) {
    const formula = normalizeFormula(value);
    return (candidates || []).some((candidate) => isFormulaMapping(String(candidate))
      && normalizeFormula(candidate) === formula);
  }
  const target = normalizeForFuzzy(value);
  return (candidates || []).some((candidate) => {
    if (isFormulaMapping(String(candidate))) return false;
    const cand = normalizeForFuzzy(candidate);
    return cand === target || fuzzyRatio(target, cand) >= FUZZY_LEARNED_THRESHOLD;
  });
};
